'use client';

import Box from '@enlighten/design-system/dist/Box';
import Stack from '@enlighten/design-system/dist/Stack';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import PanToolAltOutlinedIcon from '@mui/icons-material/PanToolAltOutlined';
import { motion, useDragControls, useMotionValue, useMotionValueEvent, useTransform } from 'motion/react';
import React from 'react';

import LandUseAfter from '@/assets/images/pages/repowering/land-use-after.png';
import LandUseAfterInfo from '@/assets/images/pages/repowering/land-use-after-info.png';
import LandUseBefore from '@/assets/images/pages/repowering/land-use-before.png';

/** 분할 바의 초기 위치 (디자인 기본값 — PC 857/1274, 모바일 230/312) */
const INITIAL_SPLIT = { desktop: 857 / 1274, mobile: 230 / 312 };
const DIVIDER_WIDTH = 4;
/** 분할 바가 이 비율(%) 이하로 오면 수치 표기 after-info 이미지로 교체 */
const AFTER_INFO_SPLIT = 45;
/** 여유부지 핀 — 원형 마커의 중심이 오는 프레임 기준 위치(%). 모바일은 사진 크롭이 좌측으로 치우쳐 여유 부지가 우측 끝에 걸린다 */
const SPARE_LAND_PIN = { left: { xs: '92%', md: '56%' }, top: '52%' };

const photoSx = {
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	maxWidth: 'none',
	objectFit: 'cover',
	// 모바일은 프레임이 세로로 길어 좌우가 크게 잘린다 — 디자인 크롭 위치에 맞춤
	objectPosition: { xs: '12% center', md: 'center' },
	pointerEvents: 'none',
} as const;

const LandUseComparison = () => {
	const frameRef = React.useRef<HTMLDivElement>(null);
	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
	const initialSplit = isMobile ? INITIAL_SPLIT.mobile : INITIAL_SPLIT.desktop;
	const splitRatioRef = React.useRef(initialSplit);
	const dragControls = useDragControls();
	const x = useMotionValue(0);
	const [showAfterInfo, setShowAfterInfo] = React.useState(false);

	const clipPath = useTransform(() => {
		// x.get() 을 조건 밖에서 먼저 읽어야 motion 이 의존성으로 등록
		const offset = x.get();
		const frameWidth = frameRef.current?.offsetWidth ?? 0;
		// 마운트 첫 평가엔 frameRef 가 비어 있음
		const ratio = frameWidth ? (offset + DIVIDER_WIDTH / 2) / frameWidth : splitRatioRef.current;

		return `inset(0 ${(1 - ratio) * 100}% 0 0)`;
	});

	const readSplitRatio = () => {
		const frameWidth = frameRef.current?.offsetWidth ?? 0;

		return frameWidth ? (x.get() + DIVIDER_WIDTH / 2) / frameWidth : splitRatioRef.current;
	};

	useMotionValueEvent(x, 'change', () => {
		setShowAfterInfo(readSplitRatio() * 100 <= AFTER_INFO_SPLIT);
	});

	// 드래그 위치는 px 라, 뷰포트 변경 시 이전 비율을 새 너비에 재적용
	React.useEffect(() => {
		const frame = frameRef.current;

		if (!frame) {
			return;
		}

		const observer = new ResizeObserver(([{ contentRect }]) => {
			x.set(contentRect.width * splitRatioRef.current - DIVIDER_WIDTH / 2);
		});

		observer.observe(frame);

		return () => {
			observer.disconnect();
		};
	}, [x]);

	// useMediaQuery 는 하이드레이션 후에야 확정돼, 그 시점의 초기 비율로 되돌린다
	React.useEffect(() => {
		splitRatioRef.current = initialSplit;
		x.set((frameRef.current?.offsetWidth ?? 0) * initialSplit - DIVIDER_WIDTH / 2);
	}, [initialSplit, x]);

	return (
		<Stack width="100%" maxWidth="1440px" marginX="auto" padding="0 24px" gap={{ xs: '16px', md: '24px' }} alignItems="center">
			<Typography fontSize={{ xs: '20px', md: '28px' }} fontWeight={700} lineHeight={{ xs: '28px', md: '38px' }} color="#6400FF" textAlign="center" sx={{ wordBreak: 'keep-all' }}>
				부지 활용 방식이 달라집니다
			</Typography>

			<Stack
				width="100%"
				gap={{ xs: '8px', md: 0 }}
				bgcolor={{ xs: 'transparent', md: 'common.white' }}
				border={{ xs: 'none', md: '1px solid #D6D7DA' }}
				borderRadius={{ xs: 0, md: '16px' }}
				overflow={{ xs: 'visible', md: 'hidden' }}
			>
				<Box padding={{ xs: 0, md: '39px 59px 40px' }}>
					<Box
						ref={frameRef}
						position="relative"
						width="100%"
						border="1px solid #D6D7DA"
						borderRadius={{ xs: '20px', md: '16px' }}
						sx={{ aspectRatio: { xs: '312 / 320', md: '1274 / 542' }, overflow: 'hidden', cursor: 'col-resize', touchAction: 'none', userSelect: 'none' }}
						onPointerDown={(event) => {
							const frame = frameRef.current;

							// snapToCursor 는 dragConstraints 를 건너뛰고 점프 → 눌린 지점을 직접 반영
							if (frame) {
								const { left, width } = frame.getBoundingClientRect();

								x.set(Math.min(width - DIVIDER_WIDTH, Math.max(0, event.clientX - left - DIVIDER_WIDTH / 2)));
								splitRatioRef.current = readSplitRatio();
							}

							dragControls.start(event);
						}}
					>
						<Box component="img" src={LandUseAfter.src} alt="리파워링 후 부지 활용 모습" sx={{ ...photoSx, opacity: showAfterInfo ? 0 : 1, transition: 'opacity 0.25s' }} />
						<Box component="img" src={LandUseAfterInfo.src} alt="리파워링 후 확보되는 여유 부지 3,500평" sx={{ ...photoSx, opacity: showAfterInfo ? 1 : 0, transition: 'opacity 0.25s' }} />

						<Box component={motion.div} position="absolute" sx={{ inset: 0 }} style={{ clipPath }}>
							<Box component="img" src={LandUseBefore.src} alt="기존 노후 발전소의 부지 활용 모습" sx={photoSx} />
							<Box
								position="absolute"
								top={{ xs: '13px', md: '20px' }}
								left={{ xs: '13px', md: '20px' }}
								bgcolor="#535659"
								borderRadius="4px"
								padding={{ xs: '4px', md: '8px' }}
								sx={{ opacity: showAfterInfo ? 0 : 1, transition: 'opacity 0.25s' }}
							>
								<Typography fontSize={{ xs: '12px', md: '14px' }} lineHeight={1.4} color="common.white" whiteSpace="nowrap">
									기존 · 6,000평 / 1,000kW
								</Typography>
							</Box>
						</Box>

						{/* 리파워링 후 요약 배지 + 말풍선 — before 쪽 '기존' 배지와 좌우 대칭 */}
						<Stack
							alignItems="flex-end"
							gap="8px"
							position="absolute"
							top={{ xs: '13px', md: '20px' }}
							right={{ xs: '13px', md: '20px' }}
							maxWidth={{ xs: '180px', md: 'none' }}
							sx={{ pointerEvents: 'none', opacity: showAfterInfo ? 1 : 0, transition: 'opacity 0.25s' }}
						>
							<Box bgcolor="#6400FF" borderRadius="4px" padding={{ xs: '4px', md: '8px' }}>
								<Typography fontSize={{ xs: '12px', md: '14px' }} lineHeight={1.4} color="common.white" textAlign="center" whiteSpace={{ xs: 'normal', md: 'nowrap' }} sx={{ wordBreak: 'keep-all' }}>
									리파워링 후 · 2,500평 / 1,000kW + 여유 3,500평
								</Typography>
							</Box>

							<Box border="1px solid #FFFFFF" borderRadius="4px" padding={{ xs: '4px', md: '8px' }} sx={{ bgcolor: 'rgba(130, 49, 255, 0.7)' }}>
								<Typography fontSize={{ xs: '12px', md: '14px' }} fontWeight={700} lineHeight={1.4} color="common.white" textAlign="center" whiteSpace="nowrap">
									리파워링 완료!
								</Typography>
								<Typography fontSize={{ xs: '12px', md: '14px' }} lineHeight={1.4} color="common.white" textAlign="center" whiteSpace={{ xs: 'normal', md: 'nowrap' }} sx={{ wordBreak: 'keep-all' }}>
									같은 6,000평에서 3,500평의 여유 부지를 확보했습니다.
								</Typography>
							</Box>
						</Stack>

						{/* 여유부지 핀 — after-info 로 바뀔 때만 노출 */}
						<Stack
							direction="row"
							alignItems="flex-start"
							position="absolute"
							left={SPARE_LAND_PIN.left}
							top={SPARE_LAND_PIN.top}
							sx={{
								// 모바일은 핀이 우측 끝이라 라벨을 마커 왼쪽으로 넘긴다
								flexDirection: { xs: 'row-reverse', md: 'row' },
								// 마커 중심을 left 기준점에 맞춘다 — 모바일은 라벨(64px)이 마커 앞에 오므로 그만큼 더 당김
								transform: { xs: 'translate(-64px, -30px)', md: 'translate(-12px, -30px)' },
								pointerEvents: 'none',
								opacity: showAfterInfo ? 1 : 0,
								transition: 'opacity 0.25s',
							}}
						>
							<Stack alignItems="center" justifyContent="center" width="24px" height="24px" marginTop="18px" bgcolor="common.white" borderRadius="50%" flexShrink={0} zIndex={1}>
								<ControlPointOutlinedIcon sx={{ width: '16px', height: '16px', color: 'rgba(100, 0, 255, 0.6)' }} />
							</Stack>
							<Stack
								alignItems="center"
								justifyContent="center"
								width={{ xs: '64px', md: '80px' }}
								height={{ xs: '46px', md: '59px' }}
								marginLeft={{ xs: 0, md: '-12px' }}
								marginRight={{ xs: '-12px', md: 0 }}
								borderRadius="4px"
								sx={{ bgcolor: 'rgba(32, 32, 32, 0.6)', overflow: 'hidden' }}
							>
								<Typography fontSize={{ xs: '12px', md: '16px' }} lineHeight={{ xs: '20px', md: '26px' }} color="common.white" whiteSpace="nowrap">
									여유부지
								</Typography>
								<Typography fontSize={{ xs: '12px', md: '16px' }} fontWeight={700} lineHeight={{ xs: '20px', md: '26px' }} color="common.white" whiteSpace="nowrap">
									3,500평
								</Typography>
							</Stack>
						</Stack>

						<Box
							component={motion.div}
							drag="x"
							dragControls={dragControls}
							dragListener={false}
							dragConstraints={frameRef}
							dragElastic={0}
							dragMomentum={false}
							style={{ x }}
							onDrag={() => {
								splitRatioRef.current = readSplitRatio();
							}}
							position="absolute"
							top={0}
							bottom={0}
							left={0}
							width={{ xs: '2px', md: `${DIVIDER_WIDTH}px` }}
							bgcolor="#F2F3F5"
							border="1px solid #F9F6FF"
						>
							<Stack alignItems="center" gap="8px" position="absolute" sx={{ top: { xs: '35%', md: '38%' }, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
								<Box bgcolor="#8231FF" borderRadius="4px" padding={{ xs: '4px 8px', md: '8px' }}>
									<Typography fontSize={{ xs: '13px', md: '14px' }} lineHeight={1.4} color="common.white" textAlign="center" whiteSpace="nowrap">
										드래그해서 확인해보세요
									</Typography>
								</Box>
								<Stack
									alignItems="center"
									justifyContent="center"
									width={{ xs: '24px', md: '40px' }}
									height={{ xs: '24px', md: '40px' }}
									bgcolor="common.white"
									borderRadius="500px"
									sx={{ filter: 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.5))' }}
								>
									<PanToolAltOutlinedIcon sx={{ width: { xs: '16px', md: '24px' }, height: { xs: '16px', md: '24px' }, color: '#6400FF' }} />
								</Stack>
							</Stack>
						</Box>

						<Box
							position="absolute"
							bottom={{ xs: '13px', md: '23px' }}
							right={{ xs: '13px', md: '17px' }}
							borderRadius="8px"
							padding="4px 8px"
							sx={{
								bgcolor: 'rgba(0, 0, 0, 0.4)',
								border: '1px solid rgba(255, 255, 255, 0.5)',
								backdropFilter: 'blur(4px)',
								pointerEvents: 'none',
							}}
						>
							<Typography fontSize={{ xs: '11px', md: '12px' }} lineHeight={{ xs: '18px', md: '20px' }} color="#93989C" whiteSpace="nowrap">
								AI로 제작된 이미지입니다.
							</Typography>
						</Box>
					</Box>
				</Box>

				<Stack
					direction="row"
					alignItems={{ xs: 'flex-start', md: 'center' }}
					justifyContent={{ xs: 'space-between', sm: 'center' }}
					gap="8px"
					bgcolor="#F9F6FF"
					borderTop={{ xs: 'none', md: '1px solid #D6D7DA' }}
					borderRadius={{ xs: '20px', md: 0 }}
					padding="16px"
				>
					<CheckCircleOutlinedIcon sx={{ width: { xs: '24px', md: '32px' }, height: { xs: '24px', md: '32px' }, margin: { xs: '4px', sm: 0 }, color: '#6400FF', flexShrink: 0 }} />
					<Typography fontSize={{ xs: '14px', sm: '18px' }} lineHeight={{ xs: '24px', sm: '28px' }} textAlign={{ xs: 'left', sm: 'center' }} sx={{ wordBreak: 'keep-all' }}>
						같은 1,000kW를 2,500평에 담고, 남은 부지는 신규 발전소 건설 또는 부지 매각으로 활용합니다.
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default LandUseComparison;
